import { ImplicitTagSource } from '@@types/api.types';
import { useDataTableFilters } from '@components/data-table-filter/hooks/use-data-table-filters';
import { MainLayout } from '@components/layouts/main.layout';
import { CompanyProfileModal } from '@components/pages/company/components/company-company-profile-modal.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { useAdminOrganizationsQuery } from '@queries/admin-organizations.query';
import { useAdminGetTags } from '@queries/admin-users.query';
import { IMPLICIT_TAG_OPTIONS, getImplicitTagSourceLabel } from '@utils/tag.util';
import { PlusIcon } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { columnsConfig } from './admin-organizations-columns.config';
import { AdminOrganizationsModal } from './components/admin-organizations-modal.component';
import { AdminOrganizationsPagination } from './components/admin-organizations-pagination.component';
import { AdminOrganizationsTable } from './components/admin-organizations-table.component';
import { useAdminOrganizationsQueryParams } from './hooks/use-admin-organizations-query-params.hook';

const normalizeTagFilterValue = (params: { source: ImplicitTagSource; value: string }): string => {
  const { source, value } = params;
  const normalizedValue = value.trim().toLowerCase();

  if (source === ImplicitTagSource.TYPE) {
    return normalizedValue.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  return normalizedValue;
};

export const AdminOrganizationsPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();

  const {
    DEFAULT_SORTING,
    filtersState,
    handleLimitChange,
    page,
    queryParams,
    search,
    selectedOrganizationId,
    setFiltersState,
    setPage,
    setSearch,
    setSelectedOrganizationId,
    setSorting,
    sortingState,
  } = useAdminOrganizationsQueryParams();

  const { data, isFetching, isPending } = useAdminOrganizationsQuery(queryParams);

  const [isCreateIntelOrgOpen, setIsCreateIntelOrgOpen] = useState(false);

  const organizations = data?.companies ?? [];
  const pagination = data?.pagination;

  // Fetch tags for filter options
  const { data: tagsData } = useAdminGetTags({ onlyUsedByCompanies: true });
  const tags = tagsData ?? [];

  // Implicit tag options (auto-generated tags)
  const implicitTagOptions = useMemo(
    () =>
      IMPLICIT_TAG_OPTIONS.map((option) => ({
        label: `${option.label} (${getImplicitTagSourceLabel(option.source)})`,
        value: normalizeTagFilterValue({ source: option.source, value: option.value }),
      })),
    [],
  );

  // Create tag options for the filter (implicit + custom)
  const tagOptions = useMemo(() => {
    const customTagOptions = tags.map((tag) => ({ label: tag.name, value: tag.name }));
    // Filter out custom tags that overlap with implicit tags (label or normalized filter key)
    const implicitAliases = new Set(
      IMPLICIT_TAG_OPTIONS.flatMap((option) => [
        option.value.trim().toLowerCase(),
        normalizeTagFilterValue({ source: option.source, value: option.value }),
      ]),
    );
    const filteredCustom = customTagOptions.filter((option) => {
      const normalizedCustomValue = option.value.trim().toLowerCase();
      const normalizedCustomTagKey = normalizedCustomValue.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      return !implicitAliases.has(normalizedCustomValue) && !implicitAliases.has(normalizedCustomTagKey);
    });
    return [...implicitTagOptions, ...filteredCustom];
  }, [tags, implicitTagOptions]);

  // Initialize data-table-filter hook
  const { actions, columns, filters, strategy } = useDataTableFilters({
    columnsConfig,
    data: organizations,
    filters: filtersState,
    onFiltersChange: setFiltersState,
    options: {
      companyTags: tagOptions,
    },
    strategy: 'server',
  });

  // Handle page bounds
  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Admin Organizations" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-6">
          <Tile
            header={{
              actions: (
                <Button onClick={() => setIsCreateIntelOrgOpen(true)}>
                  <PlusIcon className="size-3 mr-1" />
                  Create Organization
                </Button>
              ),
              title: 'Organizations',
            }}
          >
            <div className="flex flex-col gap-4">
              <AdminOrganizationsTable
                actions={actions}
                columns={columns}
                filters={filters}
                isLoading={isPending}
                onSearchChange={(value) => void setSearch(value)}
                onSelectOrganization={(orgId) => void setSelectedOrganizationId(orgId)}
                onSortingChange={(state) => {
                  if (!state || state.length === 0) {
                    void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                    return;
                  }
                  void setSorting(state);
                }}
                organizations={organizations}
                search={search}
                sorting={sortingState}
                strategy={strategy}
                tagOptions={tagOptions}
              />

              {pagination && (
                <AdminOrganizationsPagination
                  isFetching={isFetching}
                  pagination={pagination}
                  onPageChange={(nextPage) => void setPage(Math.max(1, nextPage))}
                  onPageSizeChange={handleLimitChange}
                />
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>

      <AdminOrganizationsModal
        isOpen={Boolean(selectedOrganizationId)}
        onClose={() => void setSelectedOrganizationId(null)}
        organizationId={selectedOrganizationId ?? undefined}
      />

      <CompanyProfileModal
        isOpen={isCreateIntelOrgOpen}
        mode="admin-intel"
        onClose={() => setIsCreateIntelOrgOpen(false)}
      />
    </PageviewTracker>
  );
};
